import { useEffect, useRef } from 'react';

const PARTICLES = ['💀', '✨', '🕸️', '⭐', '💜'];
const SKULL_WEIGHT = 4; // skulls appear more often

function pickParticle() {
  const pool = [];
  for (let i = 0; i < SKULL_WEIGHT; i++) pool.push('💀');
  pool.push('✨', '🕸️', '⭐', '💜');
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function CursorTrail() {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const rafRef = useRef(null);
  const lastSpawn = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const onMove = (e) => {
      const now = performance.now();
      if (now - lastSpawn.current < 40) return; // throttle: max ~25 particles/sec
      lastSpawn.current = now;

      const x = e.clientX ?? e.touches?.[0]?.clientX;
      const y = e.clientY ?? e.touches?.[0]?.clientY;
      if (x == null) return;

      particles.current.push({
        x,
        y,
        emoji: pickParticle(),
        size: 14 + Math.random() * 10,
        vx: (Math.random() - 0.5) * 1.5,
        vy: -1 - Math.random() * 1.5,
        alpha: 1,
        decay: 0.022 + Math.random() * 0.018,
        rotation: (Math.random() - 0.5) * 0.3,
        angle: 0,
      });
    };

    const onTouch = (e) => onMove(e.touches[0]);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onTouch, { passive: true });

    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.current = particles.current.filter(p => p.alpha > 0.02);

      for (const p of particles.current) {
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.font = `${p.size}px serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillText(p.emoji, 0, 0);
        ctx.restore();

        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.04; // gentle gravity
        p.alpha -= p.decay;
        p.angle += p.rotation;
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onTouch);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />
  );
}
